import React, { useCallback, useEffect, useState } from "react";
import { useGet } from "../../api";
import { IKindredAuditChange, IKindredAuditResponse } from "../../interfaces";
import {
  Accordion,
  Badge,
  Button,
  Form,
  Spinner,
  Table,
} from "react-bootstrap";
import { RefreshCw } from "react-feather";
import { format } from "date-fns";

const formatTimestamp = (ts: string | null) => {
  if (!ts) return "—";
  try {
    return format(new Date(ts), "yyyy-MM-dd HH:mm:ss");
  } catch {
    return ts;
  }
};

/** One accordion card per audited change: who, which app, which table, then column / old / new. */
export const AuditChangeList: React.FC<{
  changes: Array<
    IKindredAuditChange & {
      entityType?: string | null;
      entityLabel?: string | null;
    }
  >;
  defaultOpenFirst?: boolean;
}> = ({ changes, defaultOpenFirst }) => (
  <Accordion defaultActiveKey={defaultOpenFirst ? "0" : undefined}>
    {changes.map((change, idx) => (
      <Accordion.Item key={change.id} eventKey={String(idx)}>
        <Accordion.Header>
          <div className="d-flex justify-content-between w-100 me-3">
            <span>
              <Badge bg="secondary" className="me-2">
                {formatTimestamp(change.timestamp)}
              </Badge>
              {change.user && (
                <Badge bg="info" className="me-2">
                  {change.user}
                </Badge>
              )}
              {change.system && (
                <Badge bg="dark" className="me-2">
                  {change.system}
                </Badge>
              )}
              {change.entityLabel && (
                <Badge
                  bg="light"
                  text="dark"
                  className="me-2 border"
                  title="Which coupon, deal, offer or brand on the domain changed"
                >
                  {change.entityType ? `${change.entityType} · ` : ""}
                  {change.entityLabel}
                </Badge>
              )}
              {change.entityName && (
                <span className="text-muted small">{change.entityName}</span>
              )}
            </span>
            <Badge bg="light" text="dark">
              {change.columnChanges.length} change
              {change.columnChanges.length !== 1 ? "s" : ""}
            </Badge>
          </div>
        </Accordion.Header>
        <Accordion.Body>
          {change.comment && (
            <p className="text-muted mb-2">
              <em>{change.comment}</em>
            </p>
          )}
          {change.columnChanges.length === 0 ? (
            <span className="text-muted">No column-level changes</span>
          ) : (
            <Table size="sm" striped>
              <thead>
                <tr>
                  <th>Column</th>
                  <th>Old Value</th>
                  <th>New Value</th>
                </tr>
              </thead>
              <tbody>
                {change.columnChanges.map((col) => (
                  <tr key={col.id}>
                    <td className="fw-bold">{col.changedColumn}</td>
                    <td>
                      <code className="text-danger small">
                        {col.oldValue || "(empty)"}
                      </code>
                    </td>
                    <td>
                      <code className="text-success small">
                        {col.newValue || "(empty)"}
                      </code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Accordion.Body>
      </Accordion.Item>
    ))}
  </Accordion>
);

interface EntityAuditTrailProps {
  entityType: "deals" | "coupons" | "offers" | "brands";
  entityId: string;
  /** Compact mode for embedding in a drawer: no page header, smaller page size. */
  compact?: boolean;
}

export const EntityAuditTrail: React.FC<EntityAuditTrailProps> = ({
  entityType,
  entityId,
  compact = false,
}) => {
  const [page, setPage] = useState(1);
  const limit = compact ? 10 : 50;

  const results = useGet<IKindredAuditResponse>(
    `kindred/${entityType}/${entityId}/audit?page=${page}&limit=${limit}`,
    true
  );

  const handleRefresh = useCallback(async () => {
    await results.refetch();
  }, [results]);

  useEffect(() => {
    if (page > 1) handleRefresh();
  }, [page]);

  const pagination = results.data?.pagination;

  return (
    <div>
      <div className="d-flex justify-content-end mb-2">
        <Button
          size="sm"
          variant="outline-primary"
          onClick={async () => {
            await results.refetch();
          }}
        >
          {compact ? <RefreshCw size={14} /> : "Refresh"}
        </Button>
      </div>

      {results.loading && (
        <div className={compact ? "text-center py-3" : "text-center py-5"}>
          <Spinner animation="border" size="sm" /> Loading audit trail...
        </div>
      )}

      {results.error && (
        <div className="text-danger mb-3">Error: {results.error.message}</div>
      )}

      {results.data && results.data.changes.length === 0 && (
        <div className="text-muted">No audit trail entries found.</div>
      )}

      {results.data && results.data.changes.length > 0 && (
        <AuditChangeList changes={results.data.changes} />
      )}

      {pagination && pagination.totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <span className="small text-muted">
            Page {pagination.page} of {pagination.totalPages} (
            {pagination.total} total)
          </span>
          <div>
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page === 1}
              onClick={() => setPage(1)}
            >
              First
            </Button>{" "}
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>{" "}
            <Form.Select
              size="sm"
              className="d-inline-block w-auto"
              value={page}
              onChange={(e) => setPage(Number(e.target.value))}
            >
              {Array.from({ length: pagination.totalPages }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </Form.Select>{" "}
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page === pagination.totalPages}
              onClick={() =>
                setPage((p) => Math.min(pagination.totalPages, p + 1))
              }
            >
              Next
            </Button>{" "}
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page === pagination.totalPages}
              onClick={() => setPage(pagination.totalPages)}
            >
              Last
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
